import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@material-tailwind/react';
import clsx from 'clsx';
import { GET_INSPECTION, GET_INSPECTION_CHECK, GET_INSPECTION_COMMENTS, post } from '@services';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import Loader from '@components/widgets/Loader';
import { formatName } from '@utils/formatters';
import { useInspectionDetailStore } from '@stores';
import { InspectionStatusEnum } from '@types';

interface ApprovalRequestProps {
  onSubmit: (data: {
    comment: string;
    action: 'comment' | 'approve';
    hasException?: boolean;
    isApproved?: boolean;
  }) => Promise<void>;
  isLoading?: boolean;
  userRole: 'broker' | 'lender';
  ruleType?: string;
  ruleStatus?: string;
  showCommentBox: boolean;
}

interface Comment {
  commentId: string;
  inspectionId: string;
  text: string;
  createTime: string;
  user: {
    userId: string;
    firstName: string;
    lastName: string | null | undefined;
  };
  ruleType: string;
  isSupport: boolean;
  userRole: string;
  showCommentBox: boolean;
}

const ApprovalRequest: React.FC<ApprovalRequestProps> = ({
  onSubmit,
  isLoading = false,
  userRole,
  ruleType,
  ruleStatus,
  showCommentBox
}) => {
  const {
    inspectionDetailData,
    setInspectionDetailData,
    setInspectionChecks,
    showCommentHistory,
  } = useInspectionDetailStore();
  const isBroker = userRole === 'broker';
  const [selectedAction, setSelectedAction] = useState<'comment' | 'approve'>(
    'comment'
  );
  const [comment, setComment] = useState('');
  const [existingComments, setExistingComments] = useState<Comment[]>([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [error, setError] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const params = useParams();
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingAttemptsRef = useRef<number>(0);

  const commentsFilter = (comments: Comment[], ruleType?: string) => {
    return comments.filter((comment: Comment) => ruleType ? comment.ruleType === ruleType : false);
  }

  useEffect(() => {
    // Load existing comments
    const loadComments = async () => {
      try {
        const response = await post(
          GET_INSPECTION_COMMENTS,
          { inspectionId: params.id },
          false
        );
        if (
          response.inspectionComments &&
          Array.isArray(response.inspectionComments)
        ) {
          // Filter comments by ruleType if provided
          setExistingComments(response.inspectionComments);
        }
      } catch (error) {
        // Silently handle error
      } finally {
        setLoadingComments(false);
      }
    };

    if (params.id) {
      loadComments();
    }
  }, [params.id, ruleType, existingComments.length]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async () => {
    if (submitLockRef.current) {
      return;
    }

    // Only check for comment if it's required
    if (isCommentRequired && !comment.trim()) {
      return;
    }

    submitLockRef.current = true;
    setError('');
    setSubmitting(true);

    try {
      if (isBroker) {
        // Broker can only add comment with hasException=true
        await onSubmit({ comment, action: 'comment', hasException: true });
      } else {
        // Lender can either comment or approve
        if (selectedAction === 'comment') {
          // Lender adding comment also needs hasException=true
          await onSubmit({ comment, action: 'comment', hasException: true });
        } else {
          await onSubmit({ comment, action: 'approve', isApproved: true });
        }
      }
      // Clear comment on success
      setComment('');

      // Reload comments after successful submission
      setLoadingComments(true);
      try {
        const response = await post(
          GET_INSPECTION_COMMENTS,
          { inspectionId: params.id },
          false
        );
        if (
          response.inspectionComments &&
          Array.isArray(response.inspectionComments)
        ) {
          setExistingComments(response.inspectionComments);
        }
      } catch (error) {
        // Silently handle error
      } finally {
        setLoadingComments(false);
      }

      // Refresh inspection checks and inspection detail after submit
      try {
        const [checkResponse, inspectionResponse] = await Promise.all([
          post(GET_INSPECTION_CHECK, { inspectionId: params.id }),
          post(GET_INSPECTION, { inspectionId: params.id }, false)
        ]);

        const updatedChecks = checkResponse?.inspectionChecks
          ? {
              ...checkResponse.inspectionChecks,
              rules:
                checkResponse.rules ??
                checkResponse.inspectionChecks?.rules ??
                []
            }
          : null;

        if (updatedChecks) {
          setInspectionChecks(updatedChecks);
        }

        if (inspectionResponse?.inspection) {
          setInspectionDetailData(
            updatedChecks
              ? {
                  ...inspectionResponse.inspection,
                  inspectionChecks: updatedChecks
                }
              : inspectionResponse.inspection
          );
        }
      } catch {
        // Non-critical: will refresh on next page load
      }

      // Poll for status change until it is not RULE_BLOCKED
      const reloadStatus = async () => {
        if (pollingAttemptsRef.current >= 5) {
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
          pollingAttemptsRef.current = 0;
          return;
        }

        pollingAttemptsRef.current += 1;

        try {
          const res = await post(GET_INSPECTION, { inspectionId: params.id });
          if (res && res.inspection) {
            const newStatus = res.inspection.status;
            // Pass only the API delta — do not spread closed-over inspectionDetailData,
            // or stale inspectionChecks would overwrite rules refreshed by setInspectionChecks.
            setInspectionDetailData(res.inspection);

            if (newStatus !== InspectionStatusEnum.RULE_BLOCKED) {
              // Status changed — also refresh inspection checks for up-to-date rules
              try {
                const checkResponse = await post(GET_INSPECTION_CHECK, {
                  inspectionId: params.id
                });
                if (checkResponse?.inspectionChecks) {
                  setInspectionChecks({
                    ...checkResponse.inspectionChecks,
                    rules:
                      checkResponse.rules ??
                      checkResponse.inspectionChecks?.rules ??
                      []
                  });
                }
              } catch {
                // Non-critical: checks will refresh on next page load
              }

              if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
                pollingTimeoutRef.current = null;
              }
              pollingAttemptsRef.current = 0;
            } else {
              pollingTimeoutRef.current = setTimeout(() => {
                reloadStatus();
              }, 2000 * pollingAttemptsRef.current + 1);
            }
          }
        } catch (error) {
          if (pollingAttemptsRef.current < 5) {
            pollingTimeoutRef.current = setTimeout(() => {
              reloadStatus();
            }, 2000 * pollingAttemptsRef.current + 1);
          } else {
            pollingAttemptsRef.current = 0;
          }
        }
      };

      // Start polling to fetch the latest status if status is RULE_BLOCKED
      if (inspectionDetailData.status === InspectionStatusEnum.RULE_BLOCKED) {
        pollingAttemptsRef.current = 0; // Reset counter when starting
        reloadStatus();
      }
    } catch (error: any) {
      setError('Something went wrong while updating this inspection. Please try again in a moment or contact support if the issue continues.');
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  // Comment is required for:
  // 1. Broker users (always)
  // 2. Lender users when selecting 'comment'
  // Comment is NOT required for lender users when selecting 'approve'
  const isCommentRequired =
    isBroker ||
    (userRole === 'lender' && selectedAction === 'comment');

  const formatDate = (date: string) =>
    format(new Date(date), "dd MMMM yyyy 'at' HH:mm");

  if (loadingComments) {
    return (
      <div className="bg-gray-50 p-6 border-t border-gray-200">
        <Loader />
      </div>
    );
  }

  // Don't show input section if already approved
  const isApproved = ruleStatus === 'Approved';

  // Hide input section if there are no comments and showCommentBox is false
  if (!showCommentHistory || !ruleType) {
    return null;
  }

  const showCommentSection = existingComments.length > 0 || (showCommentBox && !isApproved);
  if (!showCommentSection) {
    return null;
  }

  const filteredComments = commentsFilter(existingComments, ruleType);

  return (
    <div className="bg-[#f9fafb] p-6 border rounded-md border-gray-200">
      <h4 className="text-gray-900 font-semibold text-base mb-4">
        {isBroker
          ? 'Add a comment for lender review'
          : 'Approval request response'}
      </h4>

      {/* Display existing comments */}
      {filteredComments.length > 0 && (
        <div className="mb-6 border-b border-gray-200 pb-6">
          <div className="space-y-3">
            {filteredComments.map((comment) => (
              <div key={comment.commentId}>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-[12px] font-medium text-[#344054]">
                    {formatName(comment.user.firstName, comment.user.lastName)}
                    {comment.userRole && (
                      <>
                        {' '}
                        (
                        {comment.userRole.charAt(0).toUpperCase() +
                          comment.userRole.slice(1).toLowerCase()}
                        )
                      </>
                    )}
                  </span>
                  <span className="text-[12px] text-gray-700">
                    {formatDate(comment.createTime)}
                  </span>
                </div>
                <p className="text-sm text-gray-700">{comment.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {showCommentBox && !isApproved && (
        <>
          {!isBroker && (
            // Lender radio buttons
            <div className="flex gap-6 mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="action"
                  value="comment"
                  checked={selectedAction === 'comment'}
                  onChange={() => setSelectedAction('comment')}
                  className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                />
                <span className="text-gray-700 text-sm">Add comment</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="action"
                  value="approve"
                  checked={selectedAction === 'approve'}
                  onChange={() => setSelectedAction('approve')}
                  className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                />
                <span className="text-gray-700 text-sm">Approve</span>
              </label>
            </div>
          )}

          <div className="mb-6 relative">
            <label
              htmlFor="comment"
              className="block text-gray-700 text-[14px] font-medium mb-1">
              Comment{' '}
              {isCommentRequired && <span className="text-red-500">*</span>}
            </label>

            <div className="relative">
              <textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 300))}
                placeholder={'Enter your comment...'}
                className={clsx(
                  'w-full px-4 py-3 border rounded-lg resize-none focus:outline-none',
                  'text-gray-900 placeholder-gray-400',
                  comment.length > 250
                    ? 'border-warning-500'
                    : 'border-gray-300'
                )}
                rows={4}
                maxLength={300}
              />

              <div className="absolute bottom-2 right-2">
                <p
                  className={clsx(
                    'text-xs',
                    comment.length > 250 ? 'text-warning-600' : 'text-gray-500'
                  )}>
                  {comment.length}/300
                </p>
              </div>
            </div>

            {isBroker && (
              <div className="mb-2">
                <p className="text-gray-600 text-[14px] mb-4">
                  Explain why this check should still be considered. Your
                  comment will be sent to the lender for review.
                </p>
              </div>
            )}
          </div>

          {error && (
            <p className="text-error-500 text-[14px] mb-4">{error}</p>
          )}

          <Button
            onClick={handleSubmit}
            disabled={
              submitting || isLoading || (isCommentRequired && !comment.trim())
            }
            className={clsx(
              'w-full py-3 font-semibold text-base normal-case',
              'bg-primary text-white hover:bg-primary-dark',
              'disabled:bg-gray-300 disabled:cursor-not-allowed'
            )}>
            {submitting || isLoading
              ? 'Submitting...'
              : isBroker
                ? 'Submit for Review'
                : 'Submit Response'}
          </Button>
        </>
      )}
    </div>
  );
};

export default ApprovalRequest;
